// ─────────────────────────────────────────────────────────────────
// Glass-i — TDS / TCS Reports page (Stage 6P.9, Phase 5C).
//
// Three roll-up reports surfaced as tabs at /erp/tds-tcs/reports.
//
// Tab 1: Supplier-wise TDS Summary
//   Columns: Supplier · GSTIN · PAN · Section · Taxable · Deducted ·
//            Reversed · Net · Challan Status · Certificate Status.
//   Filters: FY · supplier · section.
//
// Tab 2: Section-wise TDS Summary
//   Columns: Section · Tax Type · Total Taxable · Total Deducted ·
//            Total Reversed · Suppliers Count · Pending Challan Amount.
//   Filters: FY · section.
//
// Tab 3: Pending Challans
//   Columns: Section · Supplier Count · Deductions Count ·
//            Total Pending · Oldest Deduction Date.
//   Filter: FY.
//
// Backend remains the authority for ALL roll-up math — the three
// report endpoints under /api/erp/tds-tcs/reports/* compute the row
// shapes server-side. This page is a pure read surface and never
// re-aggregates locally beyond rendering the row.
//
// Style:
//   Mirrors SupplierPaymentRegisterPage — slate-50 header strip, 22 px
//   row density, sticky toolbar per tab. Reuses GoodsReceiptsPage.css
//   via the .grn-* wrapper.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Input, Select, Space, Spin, Table, Tabs, Tag,
  Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, BarChartOutlined,
  FileSearchOutlined, ClockCircleOutlined, AppstoreOutlined
} from '@ant-design/icons';

import { tdsTcsApi } from './tdsTcsApi';
import {
  TAX_TYPES,
  PERMS,
  pickField,
  formatDate,
  formatMoney,
  taxTypeLabel,
  taxTypeColor,
  challanStatusLabel,
  challanStatusColor,
  certificateStatusLabel,
  certificateStatusColor,
  deriveFinancialYear
} from './tdsTcsHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const TAX_TYPE_OPTIONS = TAX_TYPES.map((v) => ({
  value: v,
  label: taxTypeLabel(v)
}));


// ─────────────────────────────────────────────────────────────────
// Local helpers
// ─────────────────────────────────────────────────────────────────

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

function unwrapRows(out) {
  if (Array.isArray(out)) return out;
  if (Array.isArray(out?.rows))  return out.rows;
  if (Array.isArray(out?.data))  return out.data;
  if (Array.isArray(out?.items)) return out.items;
  return [];
}


// ─────────────────────────────────────────────────────────────────
// Supplier-wise TDS Summary tab
// ─────────────────────────────────────────────────────────────────

function SupplierSummaryTab({ canView, baseFy }) {
  const [financialYear, setFinancialYear] = useState(baseFy || '');
  const [supplier,      setSupplier]      = useState('');
  const [section,       setSection]       = useState('');
  const [loading,       setLoading]       = useState(false);
  const [rows,          setRows]          = useState([]);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {};
      const fy = (financialYear || '').trim();
      if (fy) params.financialYear = fy;
      const supplierNeedle = (supplier || '').trim();
      if (supplierNeedle) {
        params.supplier     = supplierNeedle;
        params.supplierName = supplierNeedle;
      }
      const sectionNeedle = (section || '').trim().toUpperCase();
      if (sectionNeedle) params.sectionCode = sectionNeedle;

      const out = await tdsTcsApi.supplierSummary(params);
      setRows(unwrapRows(out));
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load supplier summary.'),
        key: 'tds-supplier-summary'
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView, financialYear, supplier, section]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => ([
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => (
        <span>{pickField(row, 'supplier_name', 'supplierName') || '—'}</span>
      )
    },
    {
      title:     'GSTIN',
      dataIndex: 'supplierGstin',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'supplier_gstin', 'supplierGstin');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'PAN',
      dataIndex: 'supplierPan',
      width:     130,
      render: (_v, row) => {
        const v = pickField(row, 'supplier_pan', 'supplierPan');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Section',
      dataIndex: 'sectionCode',
      width:     110,
      render: (_v, row) => {
        const v = pickField(row, 'section_code', 'sectionCode');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Taxable',
      dataIndex: 'taxableAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'taxable_amount', 'taxableAmount'))}
        </span>
      )
    },
    {
      title:     'Deducted',
      dataIndex: 'deductedAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'deducted_amount', 'deductedAmount'))}
        </span>
      )
    },
    {
      title:     'Reversed',
      dataIndex: 'reversedAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'reversed_amount', 'reversedAmount'))}
        </span>
      )
    },
    {
      title:     'Net',
      dataIndex: 'netAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'net_amount', 'netAmount'))}
        </span>
      )
    },
    {
      title:     'Challan Status',
      dataIndex: 'challanStatus',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'challan_status', 'challanStatus');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={challanStatusColor(v)} className="grn-status-tag">
            {challanStatusLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Certificate Status',
      dataIndex: 'certificateStatus',
      width:     170,
      render: (_v, row) => {
        const v = pickField(row, 'certificate_status', 'certificateStatus');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={certificateStatusColor(v)} className="grn-status-tag">
            {certificateStatusLabel(v)}
          </Tag>
        );
      }
    }
  ]), []);

  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message="You don't have permission to view TDS / TCS reports."
        description={`Ask your master account to grant ${PERMS.REPORTS_VIEW}.`}
      />
    );
  }

  return (
    <div>
      <div className="grn-toolbar" style={{ marginBottom: 8 }}>
        <Input
          allowClear
          size="small"
          placeholder="Financial Year (e.g. FY-2025-26)"
          value={financialYear}
          onChange={(e) => setFinancialYear(e.target.value)}
          style={{ width: 220 }}
        />
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Supplier name / GSTIN…"
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
          style={{ width: 220 }}
        />
        <Input
          allowClear
          size="small"
          placeholder="Section (e.g. 194Q)"
          value={section}
          onChange={(e) => setSection(e.target.value)}
          style={{ width: 160 }}
        />
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button size="small" icon={<ReloadOutlined />} onClick={load} />
        </Tooltip>
      </div>
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r, idx) =>
              pickField(r, 'id', 'id')
              || `${pickField(r, 'supplier_id', 'supplierId') || ''}_${pickField(r, 'section_code', 'sectionCode') || ''}_${idx}`}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 50,
              size:     'small',
              showSizeChanger: true,
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No supplier-wise TDS / TCS data for this period.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Section-wise TDS Summary tab
// ─────────────────────────────────────────────────────────────────

function SectionSummaryTab({ canView, baseFy }) {
  const [financialYear, setFinancialYear] = useState(baseFy || '');
  const [section,       setSection]       = useState('');
  const [taxType,       setTaxType]       = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [rows,          setRows]          = useState([]);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {};
      const fy = (financialYear || '').trim();
      if (fy) params.financialYear = fy;
      const sectionNeedle = (section || '').trim().toUpperCase();
      if (sectionNeedle) params.sectionCode = sectionNeedle;
      if (taxType) params.taxType = taxType;

      const out = await tdsTcsApi.sectionSummary(params);
      setRows(unwrapRows(out));
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load section summary.'),
        key: 'tds-section-summary'
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView, financialYear, section, taxType]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => ([
    {
      title:     'Section',
      dataIndex: 'sectionCode',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'section_code', 'sectionCode');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Tax Type',
      dataIndex: 'taxType',
      width:     110,
      render: (_v, row) => {
        const v = pickField(row, 'tax_type', 'taxType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={taxTypeColor(v)} className="grn-status-tag">
            {taxTypeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Total Taxable',
      dataIndex: 'totalTaxable',
      width:     160,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'total_taxable', 'totalTaxable'))}
        </span>
      )
    },
    {
      title:     'Total Deducted',
      dataIndex: 'totalDeducted',
      width:     160,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'total_deducted', 'totalDeducted'))}
        </span>
      )
    },
    {
      title:     'Total Reversed',
      dataIndex: 'totalReversed',
      width:     160,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'total_reversed', 'totalReversed'))}
        </span>
      )
    },
    {
      title:     'Suppliers',
      dataIndex: 'suppliersCount',
      width:     110,
      align:     'right',
      render: (_v, row) => {
        const v = pickField(row, 'suppliers_count', 'suppliersCount');
        if (v === undefined || v === null) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Pending Challan Amount',
      dataIndex: 'pendingChallanAmount',
      width:     200,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'pending_challan_amount', 'pendingChallanAmount'))}
        </span>
      )
    }
  ]), []);

  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message="You don't have permission to view TDS / TCS reports."
        description={`Ask your master account to grant ${PERMS.REPORTS_VIEW}.`}
      />
    );
  }

  return (
    <div>
      <div className="grn-toolbar" style={{ marginBottom: 8 }}>
        <Input
          allowClear
          size="small"
          placeholder="Financial Year (e.g. FY-2025-26)"
          value={financialYear}
          onChange={(e) => setFinancialYear(e.target.value)}
          style={{ width: 220 }}
        />
        <Input
          allowClear
          size="small"
          placeholder="Section (e.g. 194Q)"
          value={section}
          onChange={(e) => setSection(e.target.value)}
          style={{ width: 160 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All tax types"
          value={taxType || undefined}
          onChange={(v) => setTaxType(v || null)}
          options={TAX_TYPE_OPTIONS}
          style={{ width: 140 }}
        />
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button size="small" icon={<ReloadOutlined />} onClick={load} />
        </Tooltip>
      </div>
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r, idx) =>
              pickField(r, 'id', 'id')
              || `${pickField(r, 'section_code', 'sectionCode') || ''}_${pickField(r, 'tax_type', 'taxType') || ''}_${idx}`}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 50,
              size:     'small',
              showSizeChanger: true,
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No section-wise TDS / TCS data for this period.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Pending Challans tab
// ─────────────────────────────────────────────────────────────────

function PendingChallansTab({ canView, baseFy }) {
  const [financialYear, setFinancialYear] = useState(baseFy || '');
  const [loading,       setLoading]       = useState(false);
  const [rows,          setRows]          = useState([]);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {};
      const fy = (financialYear || '').trim();
      if (fy) params.financialYear = fy;
      const out = await tdsTcsApi.pendingChallans(params);
      setRows(unwrapRows(out));
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load pending challans.'),
        key: 'tds-pending-challans'
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView, financialYear]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => ([
    {
      title:     'Section',
      dataIndex: 'sectionCode',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'section_code', 'sectionCode');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Supplier Count',
      dataIndex: 'supplierCount',
      width:     140,
      align:     'right',
      render: (_v, row) => {
        const v = pickField(row, 'supplier_count', 'supplierCount');
        if (v === undefined || v === null) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Deductions Count',
      dataIndex: 'deductionsCount',
      width:     160,
      align:     'right',
      render: (_v, row) => {
        const v = pickField(row, 'deductions_count', 'deductionsCount');
        if (v === undefined || v === null) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Total Pending',
      dataIndex: 'totalPending',
      width:     160,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {formatMoney(pickField(row, 'total_pending', 'totalPending'))}
        </span>
      )
    },
    {
      title:     'Oldest Deduction',
      dataIndex: 'oldestDeductionDate',
      width:     180,
      render: (_v, row) => (
        <span className="grn-mono">
          {formatDate(pickField(row, 'oldest_deduction_date', 'oldestDeductionDate'))}
        </span>
      )
    }
  ]), []);

  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message="You don't have permission to view TDS / TCS reports."
        description={`Ask your master account to grant ${PERMS.REPORTS_VIEW}.`}
      />
    );
  }

  return (
    <div>
      <div className="grn-toolbar" style={{ marginBottom: 8 }}>
        <Input
          allowClear
          size="small"
          placeholder="Financial Year (e.g. FY-2025-26)"
          value={financialYear}
          onChange={(e) => setFinancialYear(e.target.value)}
          style={{ width: 220 }}
        />
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button size="small" icon={<ReloadOutlined />} onClick={load} />
        </Tooltip>
      </div>
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r, idx) =>
              pickField(r, 'id', 'id')
              || `${pickField(r, 'section_code', 'sectionCode') || ''}_${idx}`}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 50,
              size:     'small',
              showSizeChanger: true,
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No pending challan amounts in this period.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function TdsTcsReportsPage() {
  const { me } = useAuth();

  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView = hasPerm(PERMS.REPORTS_VIEW);

  // Tabs default to the current FY so the operator sees data on
  // first load without having to type. They can override on a tab-by-
  // tab basis once the page is open.
  const baseFy = useMemo(() => deriveFinancialYear(new Date()), []);
  const [activeTab, setActiveTab] = useState('supplier');

  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view TDS / TCS reports."
          description={`Ask your master account to grant ${PERMS.REPORTS_VIEW}.`}
        />
      </div>
    );
  }

  const tabs = [
    {
      key:      'supplier',
      label:    (
        <span><FileSearchOutlined /> Supplier-wise</span>
      ),
      children: <SupplierSummaryTab canView={canView} baseFy={baseFy} />
    },
    {
      key:      'section',
      label:    (
        <span><AppstoreOutlined /> Section-wise</span>
      ),
      children: <SectionSummaryTab canView={canView} baseFy={baseFy} />
    },
    {
      key:      'pending',
      label:    (
        <span><ClockCircleOutlined /> Pending Challans</span>
      ),
      children: <PendingChallansTab canView={canView} baseFy={baseFy} />
    }
  ];

  return (
    <div className="grn-shell">
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><BarChartOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">TDS / TCS Reports</span>
            <span className="grn-headstrip__subtitle">
              Three roll-ups over the deduction + challan registers:
              supplier-wise summary, section-wise summary, and pending-
              challan exposure. Backend computes every row server-side
              — this page is a pure read surface, the FY filter defaults
              to the current Indian financial year.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{baseFy || '—'}</strong> default FY
          </span>
        </Space>
      </div>
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 12 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabs}
          size="small"
        />
      </Card>
    </div>
  );
}
